// src/pages/HomePage.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Link,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Accordion,
  Tooltip,
  Stack,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  School,
  Person,
  Campaign,
  Assessment,
  Menu,
  ExpandMore,
  Security,
  Policy,
  Cloud,
  ChildCare,
  Speed,
  Storage,
  Verified,
  Login,
  PersonAdd,
  Close,
  Home,
  Info,
  Build,
  ContactPhone,
  RequestQuote
} from '@mui/icons-material';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import Psychology from '@mui/icons-material/Psychology';
import Plagiarism from '@mui/icons-material/Plagiarism';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// ===========================
// Componente Feature
// ===========================
const Feature = ({ icon, title, description, isSmallScreen }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: isSmallScreen ? 2 : 3 }}>
        {React.cloneElement(icon, {
          sx: {
            fontSize: isSmallScreen ? 40 : 60,
            mb: isSmallScreen ? 1 : 2
          }
        })}
        <Typography
          variant={isSmallScreen ? 'h6' : 'h5'}
          component="div"
          sx={{ my: isSmallScreen ? 1 : 2, fontWeight: 700 }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.9rem' : '1rem' }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  </motion.div>
);

Feature.propTypes = {
  icon: PropTypes.element.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  isSmallScreen: PropTypes.bool.isRequired
};

// ===========================
// Componente Testimonial
// ===========================
const Testimonial = ({ quote, author }) => (
  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
    <CardContent>
      <Typography variant="body1" gutterBottom sx={{ fontStyle: 'italic' }}>
        &ldquo;{quote}&rdquo;
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" align="right" sx={{ mt: 2 }}>
        - {author}
      </Typography>
    </CardContent>
  </Card>
);

Testimonial.propTypes = {
  quote: PropTypes.string.isRequired,
  author: PropTypes.string.isRequired
};

// ===========================
// Componente HomeSlider
// ===========================
const HomeSlider = ({ isSmallScreen }) => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 550,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5200,
    arrows: !isSmallScreen,
    touchThreshold: 10,
    swipeToSlide: true
  };

  const slides = [
    {
      title: 'TCC & Integridade Académica',
      subtitle: 'Fluxo completo do TCC com verificação de similaridade e sinais de IA — com relatórios explicáveis.',
      image: '/images/slide-tcc.jpg',
      buttonText: 'Explorar Módulo TCC',
      buttonLink: '/tcc'
    },
     {
    title: 'Gestão de Creche',
    subtitle: 'Matrículas, check-ins diários, saúde infantil e relatórios — gestão completa para educação infantil.',
    image: '/images/creche_compri.png',
    buttonText: 'Explorar Creche',
    buttonLink: '/creche'
  },

    {
      title: 'Integridade e Conformidade',
      subtitle: 'Políticas, auditoria, perfis e permissões — confiança para decisões académicas.',
      image: '/images/slide-integridade.jpg',
      buttonText: 'Ver Integridade',
      buttonLink: '/integridade'
    },
    // Slide apenas com imagem (sem CTA)
    {
      image: '/images/hero_zenkroll_by_dbl__desktop.jpg'
    },
    {
      title: 'Campanhas de Inscrição',
      subtitle: 'Crie e faça gestão de campanhas de inscrição de forma eficiente e organizada.',
      image: '/images/slide2-campaigns.jpg',
      buttonText: 'Ver Campanhas',
      buttonLink: '/servicos'
    },
   
    {
      title: 'Relatórios e Análises',
      subtitle: 'Insights valiosos com relatórios detalhados e análises operacionais e financeiras.',
      image: '/images/slide4-reports.jpg',
      buttonText: 'Ver Relatórios',
      buttonLink: '/relatorios'
    },
    {
      title: 'Gestão Educacional Simplificada',
      subtitle: 'Transforme a forma como a sua instituição gere pré-inscrições e campanhas.',
      image: '/images/slide1-management.jpg',
      buttonText: 'Comece Agora',
      buttonLink: '/registro'
    }
  ];

  return (
    <Slider {...settings}>
      {slides.map((slide, index) => (
        <Box
          key={index}
          sx={{
            height: isSmallScreen ? '50vh' : { xs: '55vh', sm: '60vh', md: '70vh', lg: '80vh' },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 1
            }
          }}
        >
          <Box
            sx={{
              backgroundImage: `url(${slide.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, px: isSmallScreen ? 2 : 3 }}>
              {slide.title && (
                <Typography
                  variant={isSmallScreen ? 'h5' : 'h2'}
                  component="h1"
                  gutterBottom
                  sx={{
                    color: 'white',
                    textAlign: 'center',
                    fontWeight: 800,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                  }}
                >
                  {slide.title}
                </Typography>
              )}

              {slide.subtitle && (
                <Typography
                  variant={isSmallScreen ? 'body1' : 'h5'}
                  sx={{
                    mb: isSmallScreen ? 3 : 4,
                    color: 'white',
                    textAlign: 'center',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    px: isSmallScreen ? 1 : 0
                  }}
                >
                  {slide.subtitle}
                </Typography>
              )}

              {/* CTA apenas quando existir texto e link */}
              {slide.buttonText && slide.buttonLink && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size={isSmallScreen ? 'medium' : 'large'}
                    component={RouterLink}
                    to={slide.buttonLink}
                    sx={{
                      minHeight: isSmallScreen ? 44 : 48,
                      px: isSmallScreen ? 3 : 4,
                      fontSize: isSmallScreen ? '0.9rem' : '1rem',
                      fontWeight: 700,
                      boxShadow: 3
                    }}
                  >
                    {slide.buttonText}
                  </Button>
                </Box>
              )}
            </Container>
          </Box>
        </Box>
      ))}
    </Slider>
  );
};

HomeSlider.propTypes = {
  isSmallScreen: PropTypes.bool.isRequired
};

// ===========================
// Componente BannerDemo
// ===========================
const BannerDemo = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ bgcolor: 'primary.light', color: 'text.primary', py: isSmallScreen ? 4 : 8, px: 2, mt: isSmallScreen ? 4 : 8 }}>
      <Container maxWidth="md" sx={{ textAlign: 'center' }}>
        <Typography variant={isSmallScreen ? 'h5' : 'h3'} gutterBottom sx={{ fontWeight: 800 }}>
          Transforme a Gestão da Sua Instituição!
        </Typography>
        <Typography variant={isSmallScreen ? 'body1' : 'h5'} paragraph sx={{ px: isSmallScreen ? 1 : 0 }}>
          Solicite uma demonstração gratuita e descubra como o ZenKroll pode simplificar processos e reforçar a integridade académica.
        </Typography>
        <Button variant="contained" color="secondary" size="large" component={RouterLink} to="/leads" sx={{ minHeight: 48, px: 4, fontWeight: 700 }}>
          Solicitar Demonstração
        </Button>
      </Container>
    </Box>
  );
};

// ===========================
// Componente HomePage
// ===========================
const HomePage = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

const navItems = [
    { text: 'Início', path: '/', icon: <Home /> },
    { text: 'Sobre', path: '/sobre', icon: <Info /> },
    { text: 'Creche', path: '/creche', icon: <ChildCare /> },
    { text: 'Serviços', path: '/servicos', icon: <Build /> },
    { text: 'TCC', path: '/tcc', icon: <School /> },
    { text: 'Integridade', path: '/integridade', icon: <Security /> },
   
    { text: 'Contacto', path: '/contacto', icon: <ContactPhone /> }
  ];


  const actionItems = [
    { text: 'Solicitar Demonstração', path: '/leads', icon: <RequestQuote />, primary: true },
    { text: 'Login', path: '/login', icon: <Login /> },
    { text: 'Registe-se', path: '/registro', icon: <PersonAdd />, secondary: true }
  ];

  const drawer = (
    <Box sx={{ width: 280 }}>
      {/* Header do Drawer */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          ZenKroll
        </Typography>
        <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </Box>

      {/* Navegação Principal */}
      <List>
        {navItems.map((item) => (
          <ListItemButton
            key={item.text}
            component={RouterLink}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{ minHeight: 48, '&:hover': { bgcolor: 'primary.light' } }}
          >
            <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '1rem', fontWeight: 500 }} />
          </ListItemButton>
        ))}
      </List>

      {/* Ações */}
      <List>
        {actionItems.map((item) => (
          <ListItemButton
            key={item.text}
            component={RouterLink}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              minHeight: 48,
              mx: 1,
              mb: 1,
              borderRadius: 1,
              bgcolor: item.primary ? 'primary.main' : item.secondary ? 'secondary.main' : 'transparent',
              color: item.primary || item.secondary ? 'white' : 'inherit',
              '&:hover': { bgcolor: item.primary ? 'primary.dark' : item.secondary ? 'secondary.dark' : 'primary.light' }
            }}
          >
            <ListItemIcon sx={{ color: item.primary || item.secondary ? 'white' : 'primary.main', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '1rem', fontWeight: item.primary || item.secondary ? 600 : 500 }} />
          </ListItemButton>
        ))}
      </List>

      {/* Footer Drawer */}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          <strong>ZenKroll</strong> — DataBase Lda
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Top Notice: Produto da DataBase Lda */}
      <Box sx={{ bgcolor: 'grey.100', color: 'text.secondary', py: 1, px: 2, textAlign: 'center' }}>
        <Typography variant="caption">
          <strong>ZenKroll</strong> é um produto da <strong>DataBase Lda</strong>.
        </Typography>
      </Box>

      {/* AppBar */}
      <AppBar position="fixed" sx={{ backgroundColor: 'white', boxShadow: 1 }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            {/* Menu Mobile */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton size="large" aria-label="menu" onClick={handleDrawerToggle} sx={{ color: 'primary.main', mr: 1 }}>
                <Menu />
              </IconButton>
            </Box>

            {/* Logo */}
            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{ mr: 4, display: 'flex', fontWeight: 800, color: 'primary.main', textDecoration: 'none', flexGrow: { xs: 1, md: 0 } }}
            >
              ZenKroll
            </Typography>

            {/* Menu Desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.text}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: 'text.primary',
                    ml: 2,
                    minHeight: 40,
                    '&:hover': { bgcolor: 'primary.light', color: 'primary.main' }
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>

            {/* Botões Ação Desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 2 }}>
              <Button color="primary" variant="outlined" component={RouterLink} to="/login" sx={{ mr: 1, minHeight: 40, borderColor: 'primary.main', color: 'primary.main' }}>
                Login
              </Button>
              <Button color="secondary" variant="contained" component={RouterLink} to="/registro" sx={{ mr: 1, minHeight: 40 }}>
                Registe-se
              </Button>
              <Button color="primary" variant="contained" component={RouterLink} to="/leads" sx={{ minHeight: 40 }}>
                Demo
              </Button>
            </Box>

            {/* CTA Mobile */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <Button color="primary" variant="contained" size="small" component={RouterLink} to="/leads" sx={{ minHeight: 36, fontSize: '0.75rem' }}>
                Demo
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Drawer (Mobile) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, height: '100%' }
        }}
      >
        {drawer}
      </Drawer>

      {/* Spacer da AppBar fixa */}
      <Toolbar />

      {/* Slider Principal */}
      <Box sx={{ mt: { xs: 1, sm: 2 } }}>
        <HomeSlider isSmallScreen={isMobile} />
      </Box>

      {/* Banner "Solicitar Demonstração" */}
      <BannerDemo />

      {/* Secção de Features (inclui TCC, IA e Anti-Plágio) */}
      <Container maxWidth="lg" sx={{ mt: isSmallScreen ? 4 : 8, mb: 4, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={isSmallScreen ? 2 : 4}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Feature
              icon={<School color="primary" />}
              title="Instituições"
              description="Simplifique a gestão: campanhas, pré-inscrições e comunicação — tudo centralizado."
              isSmallScreen={isMobile}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Feature
              icon={<Person color="secondary" />}
              title="Estudantes"
              description="Encontra oportunidades alinhadas ao teu perfil e acompanha o estado em tempo real."
              isSmallScreen={isMobile}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Feature
              icon={<Campaign color="success" />}
              title="Campanhas de Ingresso"
              description="Planeia, segmenta e mede resultados com precisão — do anúncio à matrícula."
              isSmallScreen={isMobile}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Feature
              icon={<Assessment color="info" />}
              title="Relatórios"
              description="KPIs operacionais e financeiros com exportação para PDF, Excel e Word."
              isSmallScreen={isMobile}
            />
          </Grid>

          {/* TCC & Monografias */}
          <Grid item xs={12} sm={6} md={4}>
            <Feature
              icon={<LibraryBooks color="primary" />}
              title="TCC & Monografias"
              description="Fluxo completo (proposta → projecto → monografia → defesa), prazos, agenda e produtividade por supervisor."
              isSmallScreen={isMobile}
            />
          </Grid>

          {/* Detecção de IA */}
          <Grid item xs={12} sm={6} md={4}>
            <Feature
              icon={<Psychology color="secondary" />}
              title="Detecção de IA"
              description="Score de IA por documento com evidências explicáveis e apoio à decisão."
              isSmallScreen={isMobile}
            />
          </Grid>

          {/* Anti-Plágio */}
          <Grid item xs={12} sm={6} md={4}>
            <Feature
              icon={<Plagiarism color="error" />}
              title="Anti-Plágio"
              description="Similaridade textual por secção, painel de risco (IA + plágio + notas) e trilha de auditoria."
              isSmallScreen={isMobile}
            />
          </Grid>
        </Grid>
{/* CRECHE - NOVO */}
          <Grid item xs={12} sm={6} md={4}>
            <Feature
              icon={<ChildCare sx={{ color: '#667eea' }} />}
              title="Gestão de Creche"
              description="Matrículas, check-ins diários, saúde infantil e relatórios — gestão completa para educação infantil."
              isSmallScreen={isMobile}
            />
          </Grid>
      
        {/* Chips de Confiança/Conformidade */}
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3, flexWrap: 'wrap', gap: 1, px: { xs: 1, sm: 0 } }}>
          <Chip icon={<Security />} label="Auditoria & Perfis" variant="outlined" size={isMobile ? 'small' : 'medium'} />
          <Chip icon={<Policy />} label="Políticas & Limiares" variant="outlined" size={isMobile ? 'small' : 'medium'} />
          <Chip icon={<Storage />} label="Dados Seguros" variant="outlined" size={isMobile ? 'small' : 'medium'} />
          <Chip icon={<Cloud />} label="Escalável" variant="outlined" size={isMobile ? 'small' : 'medium'} />
          <Chip icon={<Speed />} label="Performance" variant="outlined" size={isMobile ? 'small' : 'medium'} />
          <Chip icon={<Verified />} label="Relatórios Verificáveis" variant="outlined" size={isMobile ? 'small' : 'medium'} />
        </Stack>

         {/* Mini-CTA para TCC/Integridade/Creche */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center'
          }}
        >
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/tcc"
            sx={{ minHeight: 44, px: 3, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: 300, sm: 'none' } }}
          >
            Explorar Módulo TCC
          </Button>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to="/integridade"
            sx={{ minHeight: 44, px: 3, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: 300, sm: 'none' } }}
          >
            Ver Relatórios de Integridade
          </Button>
          <Button
            variant="contained"
            startIcon={<ChildCare />}
            component={RouterLink}
            to="/pre-inscricao"
            sx={{ 
              minHeight: 44, 
              px: 3, 
              width: { xs: '100%', sm: 'auto' }, 
              maxWidth: { xs: 300, sm: 'none' },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            Pré-Inscrição Creche
          </Button>
        </Box>

        {/* Por que escolher o ZenKroll? */}
        <Typography variant={isSmallScreen ? 'h5' : 'h4'} align="center" color="textPrimary" sx={{ mt: isSmallScreen ? 6 : 8, mb: 4 }}>
          Por que escolher o ZenKroll?
        </Typography>

        <Grid container spacing={isSmallScreen ? 3 : 4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h6" gutterBottom>
                Eficiência
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Automatize o processo de pré-inscrição e o ciclo do TCC, reduzindo tempo e custos operacionais.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h6" gutterBottom>
                Transparência
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Acompanhe estados, prazos e decisões em tempo real, com registos auditáveis e relatórios claros.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h6" gutterBottom>
                Análise de Dados
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Decisões informadas com base em indicadores académicos, operacionais e de integridade.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Depoimentos */}
        <Typography variant={isSmallScreen ? 'h5' : 'h4'} align="center" color="textPrimary" sx={{ mt: isSmallScreen ? 6 : 8, mb: 4 }}>
          O que os nossos utilizadores dizem
        </Typography>

        <Grid container spacing={isSmallScreen ? 3 : 4}>
          <Grid item xs={12} md={4}>
            <Testimonial quote="O ZenKroll profissionalizou as nossas inscrições e trouxe confiança ao processo de TCC." author="Teresa Carlos, Directora de Marketing" />
          </Grid>
          <Grid item xs={12} md={4}>
            <Testimonial quote="Como estudante, a experiência tornou-se simples e clara — desde a candidatura até ao TCC." author="João Santos, Estudante" />
          </Grid>
          <Grid item xs={12} md={4}>
            <Testimonial quote="Os relatórios de integridade e as métricas de curso são hoje essenciais nas nossas decisões." author="Carlos Oliveira, Coordenador de Curso" />
          </Grid>
        </Grid>

        {/* FAQ */}
        <Box sx={{ mt: isSmallScreen ? 6 : 8 }}>
          <Typography variant={isSmallScreen ? 'h5' : 'h4'} align="center" color="textPrimary" gutterBottom>
            Perguntas Frequentes
          </Typography>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant={isMobile ? 'body1' : 'h6'}>Como funciona o processo de inscrição?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                O processo é simples: o estudante cria conta, preenche o perfil e candidata-se às campanhas disponíveis. As instituições acompanham e gerem em tempo real, com relatórios e filtros.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant={isMobile ? 'body1' : 'h6'}>Como o ZenKroll trata o TCC e a integridade académica?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                O módulo de TCC abrange proposta, orientação, revisão e defesa. A plataforma executa análises de similaridade e sinais de IA, gera evidências explicáveis, aplica políticas e limiares por curso e regista auditoria completa das decisões.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant={isMobile ? 'body1' : 'h6'}>O ZenKroll é seguro e conforme às leis de protecção de dados?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Sim. Adoptamos práticas de segurança de ponta, controlos de acesso por perfil e separação de dados por instituição. A DataBase Lda mantém padrões de conformidade e privacidade alinhados às melhores práticas.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant={isMobile ? 'body1' : 'h6'}>Quais são os custos para usar o ZenKroll?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Oferecemos planos flexíveis para instituições de diferentes portes. Entre em contacto para um orçamento alinhado às suas necessidades.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>

      {/* Chamada para Ação final */}
      <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', py: isSmallScreen ? 6 : 8, my: isSmallScreen ? 6 : 8 }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography variant={isSmallScreen ? 'h5' : 'h4'} align="center" gutterBottom sx={{ fontWeight: 800 }}>
            Pronto para elevar a integridade e a eficiência?
          </Typography>
          <Typography variant={isSmallScreen ? 'body1' : 'h6'} align="center" paragraph sx={{ px: { xs: 1, sm: 0 } }}>
            Junte-se às instituições e estudantes que já utilizam o ZenKroll — um produto da DataBase Lda.
          </Typography>
          <Box display="flex" justifyContent="center" mt={4} gap={2} flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center">
            <Button
              variant="contained"
              color="secondary"
              size="large"
              component={RouterLink}
              to="/registro"
              sx={{ minHeight: 48, px: 4, fontWeight: 700, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: 280, sm: 'none' } }}
            >
              Registe-se Gratuitamente
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              component={RouterLink}
              to="/leads"
              sx={{
                borderColor: 'primary.contrastText',
                color: 'primary.contrastText',
                minHeight: 48,
                px: 4,
                fontWeight: 700,
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: 280, sm: 'none' },
                '&:hover': { borderColor: 'primary.contrastText', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Solicitar Demonstração
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Rodapé */}
      <Container maxWidth="lg" component="footer" sx={{ mt: isSmallScreen ? 6 : 8, py: 4, bgcolor: 'grey.100', px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={4} justifyContent="space-evenly">
          <Grid item xs={6} sm={3}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Sobre
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/quem-somos" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Quem Somos
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/nossa-equipe" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  A Nossa Equipa
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/carreiras" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Carreiras
                </Link>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Contacto
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/suporte" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Suporte
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Link href="mailto:contacto@exemplo.com" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Email
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Link href="tel:+258846031855" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Telefone
                </Link>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Legal
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/privacidade" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Privacidade
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/termos-de-uso" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Termos de Uso
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/faq" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  FAQ
                </Link>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Recursos
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/blog" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Blog
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/centro-de-ajuda" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Centro de Ajuda
                </Link>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/parceiros" color="text.secondary" underline="hover" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Parceiros
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box mt={5} textAlign="center">
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.9rem' }, px: { xs: 1, sm: 0 } }}
          >
            © 2025 ZenKroll — Produto da <strong>DataBase Lda</strong>. Todos os direitos reservados. |{' '}
            <Link component={RouterLink} to="/leads" color="primary" underline="hover">
              Solicitar Demonstração
            </Link>
          </Typography>

          {/* Redes Sociais */}
          <Stack direction="row" spacing={{ xs: 1, sm: 2 }} justifyContent="center" sx={{ mt: 2 }}>
            <Tooltip title="Facebook">
              <IconButton
                component="a"
                href="https://www.facebook.com/seu-perfil"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  color: '#3b5998',
                  minHeight: 44,
                  minWidth: 44,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.2)', bgcolor: 'rgba(59,89,152,0.1)' }
                }}
              >
                <FacebookIcon fontSize={isMobile ? 'medium' : 'large'} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Instagram">
              <IconButton
                component="a"
                href="https://www.instagram.com/seu-perfil"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  color: '#E1306C',
                  minHeight: 44,
                  minWidth: 44,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.2)', bgcolor: 'rgba(225,48,108,0.1)' }
                }}
              >
                <InstagramIcon fontSize={isMobile ? 'medium' : 'large'} />
              </IconButton>
            </Tooltip>

            <Tooltip title="LinkedIn">
              <IconButton
                component="a"
                href="https://www.linkedin.com/in/seu-perfil"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  color: '#0e76a8',
                  minHeight: 44,
                  minWidth: 44,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.2)', bgcolor: 'rgba(14,118,168,0.1)' }
                }}
              >
                <LinkedInIcon fontSize={isMobile ? 'medium' : 'large'} />
              </IconButton>
            </Tooltip>

            <Tooltip title="YouTube">
              <IconButton
                component="a"
                href="https://www.youtube.com/seu-canal"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  color: '#FF0000',
                  minHeight: 44,
                  minWidth: 44,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.2)', bgcolor: 'rgba(255,0,0,0.1)' }
                }}
              >
                <YouTubeIcon fontSize={isMobile ? 'medium' : 'large'} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

export default HomePage;
